import React, { useState, useEffect } from 'react';
import { ReportFilters, getFilterOptions, FilterOptions } from '../../services/reportService';
import { CalendarIcon, FunnelIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import { sortSizes } from '../../utils/sortUtils';

interface AdvancedFiltersProps {
    filters: ReportFilters;
    onChange: (filters: ReportFilters) => void;
    onApply: () => void;
    loading?: boolean;
}

function FilterCombobox({ value, onChange, options, placeholder }: { value: string; onChange: (val: string) => void; options: string[]; placeholder: string }) {
    const [query, setQuery] = useState('');
    const filteredOptions = query === '' 
        ? options 
        : options.filter(opt => opt.toLowerCase().includes(query.toLowerCase()))
                 .sort((a, b) => {
                     const aLower = a.toLowerCase();
                     const bLower = b.toLowerCase();
                     const queryLower = query.toLowerCase();
                     if (aLower === queryLower) return -1;
                     if (bLower === queryLower) return 1;
                     if (aLower.startsWith(queryLower) && !bLower.startsWith(queryLower)) return -1;
                     if (bLower.startsWith(queryLower) && !aLower.startsWith(queryLower)) return 1;
                     return 0;
                 });

    return (
        <Combobox value={value} onChange={onChange}>
            <div className="relative">
                <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:text-sm border border-gray-300">
                    <Combobox.Input
                        className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                        displayValue={(opt: string) => opt}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={placeholder}
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </Combobox.Button>
                </div>
                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                    {filteredOptions.length === 0 && query !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">No encontrado.</div>
                    ) : (
                        filteredOptions.map((opt) => (
                            <Combobox.Option
                                key={opt}
                                className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                value={opt}
                            >
                                {({ selected, active }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{opt}</span>
                                        {selected ? (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </Combobox.Option>
                        ))
                    )}
                </Combobox.Options>
            </div>
        </Combobox>
    );
}


const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ filters, onChange, onApply, loading }) => {
    const [options, setOptions] = useState<FilterOptions>({ marcas: [], tallas: [], colores: [], vendedoras: [] });

    useEffect(() => {
        getFilterOptions().then((data) => {
            setOptions({
                ...data,
                tallas: sortSizes(data.tallas)
            });
        }).catch(console.error);
    }, []);

    const handleChange = (field: keyof ReportFilters, value: string) => {
        onChange({ ...filters, [field]: value });
    };

    const handleClear = () => {
        onChange({
            startDate: filters.startDate,
            endDate: filters.endDate
            // Keep dates, clear rest
        });
        setTimeout(onApply, 100);
    };

    return (
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="flex items-center gap-2 mb-4 text-indigo-700">
                <FunnelIcon className="w-5 h-5" />
                <h3 className="font-semibold">Filtros Avanzados</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Fechas */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Fecha Desde</label>
                    <div className="relative">
                        <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="date"
                            value={filters.startDate || ''}
                            onChange={(e) => handleChange('startDate', e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Fecha Hasta</label>
                    <div className="relative">
                        <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="date"
                            value={filters.endDate || ''}
                            onChange={(e) => handleChange('endDate', e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>

                {/* Producto */}
                <div className="space-y-1 relative">
                    <label className="text-xs font-medium text-gray-500">Marca</label>
                    <FilterCombobox
                        value={filters.marca || ''}
                        onChange={(val) => handleChange('marca', val)}
                        options={options.marcas}
                        placeholder="Buscar marca..."
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Modelo / Código</label>
                    <input
                        type="text"
                        placeholder="Ej. VEST-01"
                        value={filters.modelo || ''}
                        onChange={(e) => handleChange('modelo', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                </div>
                <div className="space-y-1 relative">
                    <label className="text-xs font-medium text-gray-500">Talla</label>
                    <FilterCombobox
                        value={filters.talla || ''}
                        onChange={(val) => handleChange('talla', val)}
                        options={options.tallas}
                        placeholder="Buscar talla..."
                    />
                </div>
                <div className="space-y-1 relative">
                    <label className="text-xs font-medium text-gray-500">Color</label>
                    <FilterCombobox
                        value={filters.color || ''}
                        onChange={(val) => handleChange('color', val)}
                        options={options.colores}
                        placeholder="Buscar color..."
                    />
                </div>

                {/* Personas y Estado */}
                <div className="space-y-1 relative">
                    <label className="text-xs font-medium text-gray-500">Vendedora</label>
                    <FilterCombobox
                        value={filters.vendedora || ''}
                        onChange={(val) => handleChange('vendedora', val)}
                        options={options.vendedoras}
                        placeholder="Buscar vendedora..."
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500">Estado</label>
                    <select
                        value={filters.estado || ''}
                        onChange={(e) => handleChange('estado', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
                    >
                        <option value="">Cualquier estado</option>
                        <option value="COMPLETED">Completada</option>
                        <option value="PENDING">Pendiente</option>
                        <option value="APARTADO">Apartado</option>
                        <option value="PRE-ORDER">Pre-order</option>
                        <option value="CANCELLED">Cancelada</option>
                    </select>
                </div>
            </div>

            <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                    onClick={handleClear}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                    Limpiar Filtros
                </button>
                <button
                    onClick={onApply}
                    disabled={loading}
                    className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors flex items-center gap-2"
                >
                    {loading ? (
                        <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Aplicando...
                        </>
                    ) : 'Aplicar Filtros'}
                </button>
            </div>
        </div>
    );
};

export default AdvancedFilters;
